package top.wcpe.allincore.dsl.compiler.diag

import top.wcpe.allincore.dsl.compiler.source.SourcePosition
import top.wcpe.allincore.dsl.compiler.source.SourceRange

/**
 * 诊断收集器。语义阶段、词法阶段、解析阶段共享同一收集器。
 *
 * 仅做收集与查询，不做格式化输出；输出由调用方决定。
 *
 * M5-f：可选注入 [sourceProvider]，用于在上报诊断时附加源码片段与填充后的 fixHint，
 * 供后续富格式渲染使用。未注入时行为与旧版完全一致（sourceSnippet / fixHint 均为 null）。
 */
class DiagnosticCollector(
    private val sourceProvider: SourceProvider? = null,
) {
    private val items = mutableListOf<Diagnostic>()

    /** 直接上报一条诊断。 */
    fun report(d: Diagnostic) {
        items += d
    }

    /** 上报一条 ERROR 级别诊断。 */
    @Deprecated(
        message = "改用 error(DslError, SourcePosition, vararg args)；旧字符串重载仅供迁移期临时共存。",
        replaceWith = ReplaceWith("error(/* TODO: DslError.E... */, position, message)"),
        level = DeprecationLevel.WARNING,
    )
    fun error(code: String, message: String, position: SourcePosition) =
        report(Diagnostic(code, DiagnosticSeverity.ERROR, message, position))

    /** 上报一条 WARNING 级别诊断。 */
    @Deprecated(
        message = "改用 warning(DslError, SourcePosition, vararg args)；旧字符串重载仅供迁移期临时共存。",
        replaceWith = ReplaceWith("warning(/* TODO: DslError.E... */, position, message)"),
        level = DeprecationLevel.WARNING,
    )
    fun warning(code: String, message: String, position: SourcePosition) =
        report(Diagnostic(code, DiagnosticSeverity.WARNING, message, position))

    /** 上报一条 ERROR 级别诊断：用 [DslError] 枚举 + 占位符参数生成消息。 */
    fun error(err: DslError, position: SourcePosition, vararg args: Any?) =
        report(buildDiagnostic(err, DiagnosticSeverity.ERROR, position, args))

    /** 上报一条参数已封装为数组的 ERROR 级别诊断。 */
    internal fun errorWithArgs(err: DslError, position: SourcePosition, args: Array<out Any?>) =
        report(buildDiagnostic(err, DiagnosticSeverity.ERROR, position, args))

    /** 上报一条 WARNING 级别诊断：用 [DslError] 枚举 + 占位符参数生成消息。 */
    fun warning(err: DslError, position: SourcePosition, vararg args: Any?) =
        report(buildDiagnostic(err, DiagnosticSeverity.WARNING, position, args))

    /** 上报一条带迁移目标的 WARNING 级诊断。 */
    fun warning(
        err: DslError,
        position: SourcePosition,
        migration: MigrationHint,
        vararg args: Any?,
    ) = report(buildDiagnostic(err, DiagnosticSeverity.WARNING, position, args, migration))

    /**
     * 上报一条 INFO 级别诊断：用 [DslError] 枚举 + 占位符参数生成消息。
     *
     * INFO 级别在 M9 Stage 6 引入，专用于"建议性"诊断（如形参二次赋值、流敏感简化的
     * 可空解引用提醒等）。IDE 端会映射为 WEAK_WARNING 不出红 / 黄波浪线，避免对历史
     * 样本造成误报洪水。
     */
    fun info(err: DslError, position: SourcePosition, vararg args: Any?) =
        report(buildDiagnostic(err, DiagnosticSeverity.INFO, position, args))

    /**
     * 统一构造 [Diagnostic]：填充 message、sourceSnippet、fixHint 三个与渲染相关的字段。
     *
     * - message 始终由 [DslError.format] 填充
     * - sourceSnippet 仅当注入了 [sourceProvider] 且能成功抓取时才附加
     * - fixHint 仅当 [DslError.fixHint] 非空时才填充，支持与 message 共用同一组占位符实参
     */
    private fun buildDiagnostic(
        err: DslError,
        severity: DiagnosticSeverity,
        position: SourcePosition,
        args: Array<out Any?>,
        migration: MigrationHint? = null,
    ): Diagnostic {
        val snippet = sourceProvider?.fetchSnippet(position)
        val hint = err.formatFixHint(*args)
        return Diagnostic(
            code = err.code,
            severity = severity,
            message = err.format(*args),
            position = position,
            sourceSnippet = snippet,
            fixHint = hint,
            migration = migration,
        )
    }

    /** 返回当前已收集到的所有诊断（快照副本，保持收集器既有顺序语义）。 */
    fun all(): List<Diagnostic> = items.toList()

    /** 编译结果使用的稳定排序且去重诊断快照。 */
    fun stableAll(): List<Diagnostic> = items
        .distinctBy { diagnostic ->
            DiagnosticKey(
                code = diagnostic.code,
                severity = diagnostic.severity,
                message = diagnostic.message,
                range = diagnostic.range,
                fixHint = diagnostic.fixHint,
            )
        }
        .sortedWith(diagnosticComparator)

    private data class DiagnosticKey(
        val code: String,
        val severity: DiagnosticSeverity,
        val message: String,
        val range: SourceRange,
        val fixHint: String?,
    )

    private companion object {
        val diagnosticComparator = compareBy<Diagnostic>(
            { it.range.sourceId },
            { if (it.range == SourceRange.NONE) 1 else 0 },
            { it.range.start },
            { it.range.length },
            { it.severity.ordinal },
            { it.code },
            { it.message },
            { it.fixHint != null },
            { it.fixHint ?: "" },
        )
    }

    /** 是否存在 ERROR 级别诊断。 */
    fun hasErrors(): Boolean = items.any { it.severity == DiagnosticSeverity.ERROR }
}
